import { CallbackButton } from '@app/app/_components/CallbackButton/CallbackButton';
import { Card } from '@app/app/_components/Card/Card';
import { Heading } from '@app/app/_components/Heading/Heading';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { BeneficiaryIcon } from '@app/app/_icons/BeneficiaryIcon';
import { DeleteIcon } from '@app/app/_icons/DeleteIcon';
import { EditIcon } from '@app/app/_icons/EditIcon';
import { BeneficiaryData } from '@app/domain/model/policy/BeneficiaryData';
import { useTranslations } from 'next-intl';

interface Props {
    beneficiary: BeneficiaryData;
    isExisting: boolean;
    onEdit?: (beneficiaryId: string) => void;
    onDelete?: (beneficiaryId: string) => void;
}

export function BeneficiaryCard({ beneficiary, isExisting, onEdit, onDelete }: Props) {
    const t = useTranslations();

    const getDisplayName = () => {
        if (beneficiary.firstName && beneficiary.lastName) {
            return `${beneficiary.firstName} ${beneficiary.lastName}`;
        }

        if (beneficiary.firstName || beneficiary.lastName) {
            return beneficiary.firstName || beneficiary.lastName;
        }

        if (beneficiary.name) {
            return beneficiary.name;
        }

        if (isExisting) {
            return beneficiary.personalCode;
        }

        return t('lifeInsurance.newBeneficiary');
    };

    const displayName = getDisplayName();
    const sharePercentage = beneficiary.share ? `${Math.round(beneficiary.share * 100)}%` : '0%';

    const personalCodeValue = beneficiary.personalCode || t('general.notSet');

    return (
        <Card className="flex w-full flex-col p-6">
            <div className="flex items-center justify-between border-b border-neutral-150 pb-4">
                <div className="flex items-center gap-2">
                    <BeneficiaryIcon className="size-10 text-primary-500" />
                    <div className="flex flex-col">
                        <Heading level="h4">{displayName}</Heading>
                    </div>
                </div>
                <div className="flex flex-row gap-2">
                    {onEdit && (
                        <CallbackButton
                            variant="text"
                            className={isExisting ? 'p-0' : 'text-primary-500'}
                            onClick={onEdit}
                            id={beneficiary.id}
                            rightAddon={<EditIcon className="size-4" />}>
                            {t('general.edit')}
                        </CallbackButton>
                    )}
                    {onDelete && (
                        <CallbackButton
                            variant="text"
                            onClick={onDelete}
                            id={beneficiary.id}
                            className="p-0 text-red-500"
                            rightAddon={<DeleteIcon className="size-4" />}>
                            {t('general.delete')}
                        </CallbackButton>
                    )}
                </div>
            </div>
            <div className="mt-4 grid grid-cols-2">
                <ReadOnlyField label={t('general.personalCode')}>{personalCodeValue}</ReadOnlyField>
                <ReadOnlyField label={t('general.percentage')}>{sharePercentage}</ReadOnlyField>
            </div>
        </Card>
    );
}
